import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { accountDeletionApi } from '@api/endpoints/accountDeletion'
import { pendingDeletionFrom } from '@features/profile/settings/accountDeletion'
import { useAuthStore } from '@store/authStore'

export const ACCOUNT_DELETION_QUERY_KEY = ['me', 'account-deletion'] as const

/**
 * Whether this account is scheduled for deletion. Asks the deletion endpoint, and also believes a
 * `pendingDeletion` the sign-in payload carried, so the prompt can show before the request lands.
 * A failed request (an API that has not shipped the endpoint yet, say) counts as "none": the
 * prompt it drives blocks the page, so it must only ever appear for a deletion that is real.
 */
export function useAccountDeletion(options?: { enabled?: boolean }) {
  const loginPending = useAuthStore((s) => s.user?.pendingDeletion ?? null)
  const fromLogin = pendingDeletionFrom({ pendingDeletion: loginPending })
  const query = useQuery({
    queryKey: ACCOUNT_DELETION_QUERY_KEY,
    queryFn: async () => {
      try {
        return pendingDeletionFrom(await accountDeletionApi.get())
      } catch {
        return null
      }
    },
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    retry: false,
  })
  const pending = query.isSuccess ? query.data : fromLogin
  return { pending, isLoading: query.isLoading }
}

/** Cancel a scheduled deletion; the account carries on as if it had never been asked. */
export function useCancelAccountDeletion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => accountDeletionApi.cancel(),
    onSuccess: () => {
      queryClient.setQueryData(ACCOUNT_DELETION_QUERY_KEY, null)
      if (useAuthStore.getState().user?.pendingDeletion) {
        useAuthStore.getState().updateUser({ pendingDeletion: null })
      }
    },
  })
}
